import React from 'react';
import { OverlayTrigger,  Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames/bind';
import styles from './DeliveryDetails.module.scss';
import PropTypes from 'prop-types';
import { generatePaletteForDeliveries } from 'utils/station/wetstock/deliveries/generateColourPalette';
import { useCompanyTimeZone } from 'components/common/hooks/useCompanyTimeZone';
import moment from 'moment';
import { useAuth } from 'config/context/authContext';

const cx = classNames.bind(styles);

const DeliveryDetails = ({ level, colour, style, deliveriesCount, details, locale }) => {
  const { userLocale } = useAuth()
  const { companyTimeZone } = useCompanyTimeZone()
  const dateFormatString = userLocale === 'en-US' ? 'MMM DD, YYYY' : 'DD MMM YYYY'

  const { t } = useTranslation()

  if (!details) {
    return null
  }

  const selectedPalette = generatePaletteForDeliveries(colour, deliveriesCount)
  const backgroundColor = selectedPalette[deliveriesCount - level] || '#000000'
  const isFallback = backgroundColor === '#000000'
  const primaryBorderColor = selectedPalette[0]

  const overlay = (
    <Tooltip className={cx('tooltip')}>
      <div className={cx('info-rows')}>
        <div className={cx('info-row')}>
           <div className={cx('info-item', 'left')}>
            <div className={cx('title')}>{t('widgets.wetstockAnalysis.deliveryDate')}</div>
            <div className={cx('value')}>{moment.utc(details.date).tz(companyTimeZone).format(dateFormatString)}</div>
          </div>
          <div className={cx('info-item', 'right')}>
            <div className={cx('title')}>{t('widgets.wetstockAnalysis.unitPrice')}</div>
            <div className={cx('value')}>
              {Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(details.unitPrice)}
              {t('margin.short')}
            </div>

          </div>
        </div>
        <hr/>

        <div className={cx('info-row')}>
          <div className={cx('info-item', 'left')}>
            <div className={cx('title')}>{t('widgets.wetstockAnalysis.deliveredVolume')}</div>
            <div className={cx('value')}>{Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(details.deliveredVolume)} {t('volume.short')}</div>
          </div>
          <div className={cx('info-item', 'right')}>
            <div className={cx('title')}>{t('widgets.wetstockAnalysis.costOfDelivery')}</div>
            <div className={cx('value')}>
              {t('currency.short')}
              {Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(details.costOfDelivery / 100)}
              </div>
          </div>
        </div>
        <div className={cx('info-row')}>
          <div className={cx('info-item', 'left')}>
            <div className={cx('title')}>{t('widgets.wetstockAnalysis.remainingVolume')}</div>
            <div className={cx('value')}>{Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(details.remainingVolume)} {t('volume.short')}</div>
          </div>
          <div className={cx('info-item', 'right')}>
            <div className={cx('title')}>{t('widgets.wetstockAnalysis.remainingValue')}</div>
            <div className={cx('value')}>
              {t('currency.short')}
              {Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(details.remainingValue / 100)}
              </div>
          </div>
        </div>
      </div>
    </Tooltip>
  );

  return (
    <OverlayTrigger placement="top" overlay={overlay}>
      <div
        style={{
          ...style,
          backgroundColor,
          ...(isFallback ? { borderTop: `1px solid ${primaryBorderColor}` } : {}),
        }}
      >
      </div>
    </OverlayTrigger>
  );
};

DeliveryDetails.propTypes = {
  colour: PropTypes.string,
  deliveriesCount: PropTypes.number,
  level: PropTypes.number,
  style: PropTypes.object,
  details: PropTypes.object,
  locale: PropTypes.string,
}

export { DeliveryDetails };
